import type { ComponentProps } from "react";
import { NavLink, useLocation } from "react-router";
import { House, LogOut, MapPinned, Package, Settings } from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { logout, type Me } from "@/api";

const NAV_ITEMS = [
  { title: "Home", url: "/", icon: House },
  { title: "Shipment Explorer", url: "/shipments", icon: MapPinned },
  { title: "Order Explorer", url: "/orders", icon: Package },
];

export interface AppSidebarProps extends ComponentProps<typeof Sidebar> {
  me: Me;
}

/** Adapted from prism-next's sidebar-01/dashboard-01 blocks — nav items replace
 *  the old top tab-bar (main.ts's #seTabs), the account dropdown moves from
 *  the header into a NavUser-style sidebar footer. */
export function AppSidebar({ me, ...props }: AppSidebarProps) {
  const location = useLocation();

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild className="data-[slot=sidebar-menu-button]:p-1.5!">
              <NavLink to="/">
                {/* Certu brand MARK (logo glyph only, no wordmark): colored in light
                    mode, monochrome (inverted to white) in dark mode — the two-<img>
                    swap convention from ops-agent-astro's sidebar. The mono SVG is
                    fill:currentColor, so dark:invert flips its default black to white. */}
                <img src="/certu-mark.svg" alt="Certu" className="h-6 w-auto shrink-0 dark:hidden" />
                <img
                  src="/certu-mark-mono.svg"
                  alt=""
                  aria-hidden="true"
                  className="hidden h-6 w-auto shrink-0 dark:block dark:invert"
                />
                <span className="text-base font-semibold">Explorer</span>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {NAV_ITEMS.map((item) => (
                <SidebarMenuItem key={item.url}>
                  <SidebarMenuButton asChild isActive={location.pathname === item.url} tooltip={item.title}>
                    <NavLink to={item.url}>
                      <item.icon />
                      <span>{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
              {me.isAdmin && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={location.pathname === "/admin"} tooltip="Admin">
                    <NavLink to="/admin">
                      <Settings />
                      <span>Admin</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <Avatar className="size-8 rounded-lg">
                    <AvatarFallback className="rounded-lg">{me.email.slice(0, 2).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{me.email}</span>
                    <span className="truncate text-xs text-muted-foreground">{me.isAdmin ? "Admin" : "Member"}</span>
                  </div>
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
                side="right"
                align="end"
                sideOffset={4}
              >
                <DropdownMenuLabel className="font-normal text-muted-foreground">{me.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => void logout()}>
                  <LogOut /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
